import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useMemo } from 'react';
import { type FieldErrors, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReviewModalNotes } from '@/features/DataChangeRequest/ReviewModalContent/ReviewModalNotes/ReviewModalNotes';

import { FormValuesIn } from '../DataChangeRequest';

import { dcrModalDialogActionsStyles as sx } from './DcrModalDialogActions.styles';

type Props = {
	onClose: () => void;
	onSeeReview: () => void;
	isDirty: boolean;
	isValid: boolean;
	isSimpleView: boolean;
	errors: FieldErrors<FormValuesIn>;
};

export const DcrModalDialogActions = ({
	onClose,
	onSeeReview,
	isDirty,
	isValid,
	isSimpleView,
	errors
}: Props) => {
	const { t } = useTranslation();
	const { control } = useFormContext();
	const isInvalid = !isDirty || !isValid;

	const errorMessage = useMemo(
		() =>
			errors?.addresses?.type === 'custom' ? errors.addresses?.message : null,
		// Note (Ben): errors is a proxy object and therefore cannot trigger the useMemo.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(errors)]
	);

	const hasErrors = useMemo(
		() => Object.keys(errors).length > 0,
		// Note (Ben): errors is a proxy object and therefore cannot trigger the useMemo.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(errors)]
	);

	const addresses = useWatch({ control, name: 'addresses' }) || [];
	const affiliations = useWatch({ control, name: 'parenthcos' }) || [];

	const showRequirementsWarning = useMemo(() => {
		const hasFieldErrors = Object.keys(errors).length > 0;
		if (hasFieldErrors) return false;
		return addresses.length === 0 && affiliations.length === 0;
		// Note (Ben): errors is a proxy object and therefore cannot trigger the useMemo.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		addresses.length,
		affiliations.length,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		JSON.stringify(errors)
	]);

	return (
		<Box sx={sx.root}>
			{!isSimpleView && <ReviewModalNotes control={control} expandOnFocus />}

			<Box sx={sx.footer}>
				{showRequirementsWarning && !hasErrors && isInvalid && (
					<Box sx={sx.errorContainer}>
						<Alert sx={sx.error} severity='warning'>
							{t('Either an Address or an Affiliation is required')}
						</Alert>
					</Box>
				)}

				{hasErrors && isInvalid && (
					<Box sx={sx.errorContainer}>
						<Alert sx={sx.error} severity='error'>
							{errorMessage
								? errorMessage
								: t('Please correct the highlighted fields')}
						</Alert>
					</Box>
				)}

				<Box sx={sx.actions}>
					<Button variant='text' onClick={onClose}>
						{t('Cancel')}
					</Button>
					<Button
						disabled={isInvalid}
						variant='contained'
						onClick={onSeeReview}
					>
						{t('Review')}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
